/** A polygon from pasted or uploaded GeoJSON or KML (spec §6.3). */
import type { LngLat } from '@/planner/types';

export type ParseResult = { ok: true; polygon: LngLat[] } | { ok: false; reason: 'no_polygon' | 'invalid' | 'too_many_vertices' };

/** The backend's cap on a mission polygon (backend/missions/schemas.py). */
const MAX_VERTICES = 500;

function found(ring: LngLat[] | null): ParseResult {
  if (!ring) return { ok: false, reason: 'no_polygon' };
  return ring.length > MAX_VERTICES ? { ok: false, reason: 'too_many_vertices' } : { ok: true, polygon: ring };
}

function clean(ring: unknown): LngLat[] | null {
  if (!Array.isArray(ring)) return null;
  const pts = ring.filter((p): p is number[] => Array.isArray(p) && p.length >= 2 && Number.isFinite(p[0]) && Number.isFinite(p[1])).map((p) => [p[0], p[1]] as LngLat);
  if (pts.length >= 2) {
    const [a, b] = [pts[0], pts[pts.length - 1]];
    if (a[0] === b[0] && a[1] === b[1]) pts.pop();
  }
  return pts.length >= 3 ? pts : null;
}

function fromGeoJson(node: unknown): LngLat[] | null {
  if (!node || typeof node !== 'object') return null;
  const g = node as { type?: string; coordinates?: unknown; geometry?: unknown; features?: unknown[]; geometries?: unknown[] };
  switch (g.type) {
    case 'Polygon':
      return clean((g.coordinates as unknown[])?.[0]);
    case 'MultiPolygon':
      for (const poly of (g.coordinates as unknown[][]) ?? []) {
        const r = clean(poly?.[0]);
        if (r) return r;
      }
      return null;
    case 'Feature':
      return fromGeoJson(g.geometry);
    case 'FeatureCollection':
      for (const f of g.features ?? []) {
        const r = fromGeoJson(f);
        if (r) return r;
      }
      return null;
    case 'GeometryCollection':
      for (const gg of g.geometries ?? []) {
        const r = fromGeoJson(gg);
        if (r) return r;
      }
      return null;
    default:
      return null;
  }
}

function fromKml(text: string): LngLat[] | null | 'invalid' {
  const doc = new DOMParser().parseFromString(text, 'text/xml');
  if (doc.getElementsByTagName('parsererror').length) return 'invalid';
  const polygons = Array.from(doc.getElementsByTagNameNS('*', 'Polygon'));
  for (const poly of polygons) {
    const outer = poly.getElementsByTagNameNS('*', 'outerBoundaryIs')[0] ?? poly;
    const coords = outer.getElementsByTagNameNS('*', 'coordinates')[0]?.textContent ?? '';
    const ring = coords
      .trim()
      .split(/\s+/)
      .map((tuple) => tuple.split(',').map(Number));
    const r = clean(ring);
    if (r) return r;
  }
  return null;
}

export function parsePolygon(text: string): ParseResult {
  const trimmed = text.trim();
  if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
    try {
      return found(fromGeoJson(JSON.parse(trimmed)));
    } catch {
      return { ok: false, reason: 'invalid' };
    }
  }
  if (trimmed.startsWith('<')) {
    const ring = fromKml(trimmed);
    if (ring === 'invalid') return { ok: false, reason: 'invalid' };
    return found(ring);
  }
  return { ok: false, reason: 'invalid' };
}
